// ─── Markdown document attachments ───────────────────────────────────────────────
//
// Turns a Markdown file the agent "sent" into a clean attachment card in the chat;
// tapping it opens the doc in the split panel (see lib/documentViewer + DocumentPanel).
// Three capture paths, all funnelling into the same DocCard:
//   A  tool-call: the agent wrote/read a .md file  → handled in turns/TurnItem
//   B  inline:    the agent printed a ```markdown block straight into the chat
//                 → extractInlineDocs() lifts it out so the raw dump never shows
//   •  path/link: the agent mentioned a .md path or a .md URL in its prose
//                 → detectFileDocs() finds them (code spans/blocks excluded)
//
// Like imageAttach, detection is ORTHOGONAL to the content-type plugin: renderContent
// pulls docs out, renders whatever text remains normally, then appends the cards below.

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { Icon } from '../components/Icon'
import { openDocument, docKey, type DocRef } from '../lib/documentViewer'

// ─── B: inline ```markdown blocks ────────────────────────────────────────────────

const FENCE_RE = /```(?:markdown|md)\b[^\n]*\n([\s\S]*?)```/gi

/** Derive a filename for an inline doc from its first heading, else a numbered default. */
function inferDocName(body: string, index: number): string {
  const m = body.match(/^#{1,6}\s+(.+)$/m)
  if (m) {
    const slug = m[1].trim().toLowerCase()
      .replace(/[^\w฀-๿]+/g, '-')   // keep word chars + Thai, collapse the rest to '-'
      .replace(/^-+|-+$/g, '')
      .slice(0, 40)
    if (slug) return `${slug}.md`
  }
  return index === 0 ? 'document.md' : `document-${index + 1}.md`
}

/** Lift ```markdown fenced blocks out of `content` into inline DocRefs; return them + the rest. */
export function extractInlineDocs(content: string): { docs: DocRef[]; clean: string } {
  const docs: DocRef[] = []
  FENCE_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = FENCE_RE.exec(content)) !== null) {
    const body = m[1].trim()
    if (body) docs.push({ kind: 'inline', name: inferDocName(body, docs.length), content: body })
  }
  if (docs.length === 0) return { docs, clean: content }
  const clean = content.replace(FENCE_RE, '').replace(/\n{3,}/g, '\n\n').trim()
  return { docs, clean }
}

// ─── path / link: .md references in prose ─────────────────────────────────────────

// Strip fenced + inline code first so a path INSIDE code stays plain text (the user
// asked: don't touch paths in code blocks).
function stripCode(s: string): string {
  return s.replace(/```[\s\S]*?```/g, ' ').replace(/`[^`]*`/g, ' ')
}

const URL_RE  = /\bhttps?:\/\/[^\s<>"')\]]+\.md\b/gi
// A path-like token ending in .md, anchored on a non-identifier boundary so we don't
// start mid-word and don't match ".md" alone or "foo.markdown". No lookbehind (mobile WebView).
const PATH_RE = /(?:^|[^\w/.])([\w.\-/~]+\.md)(?=$|[^\w])/g

/** Find .md URLs + on-disk .md paths referenced in prose (code excluded). Deduped. */
export function detectFileDocs(content: string): DocRef[] {
  const text = stripCode(content)
  const seen = new Set<string>()
  const refs: DocRef[] = []

  let m: RegExpExecArray | null
  URL_RE.lastIndex = 0
  while ((m = URL_RE.exec(text)) !== null) {
    const url = m[0]
    if (seen.has(url)) continue
    seen.add(url)
    refs.push({ kind: 'url', name: url.split('/').pop() || 'document.md', src: url })
  }

  // Blank out the URLs we already took so their trailing path isn't re-matched as a file.
  const noUrls = text.replace(URL_RE, ' ')
  PATH_RE.lastIndex = 0
  while ((m = PATH_RE.exec(noUrls)) !== null) {
    const p = m[1]
    if (seen.has(p)) continue
    seen.add(p)
    refs.push({ kind: 'file', name: p.split('/').pop() || p, src: p })
  }
  return refs
}

// ─── Card + list ─────────────────────────────────────────────────────────────────

function DocCard({ docRef, chatId }: { docRef: DocRef; chatId?: string }) {
  // A file card must prove the file exists before it offers to open (the user asked for
  // "only real files"). url/inline are always shown — there's nothing to pre-verify.
  const [state, setState] = useState<'show' | 'checking' | 'missing'>(
    docRef.kind === 'file' ? 'checking' : 'show',
  )

  useEffect(() => {
    if (docRef.kind !== 'file' || !chatId || !docRef.src) return
    let alive = true
    api.chats.statDoc(chatId, docRef.src)
      .then(r => { if (alive) setState(r.exists ? 'show' : 'missing') })
      .catch(() => { if (alive) setState('missing') })
    return () => { alive = false }
  }, [docRef.kind, docRef.src, chatId])

  if (state !== 'show') return null

  const sub = docRef.kind === 'url' ? 'Markdown · link' : 'Markdown'
  return (
    <button
      type="button"
      className="doc-card"
      onClick={() => openDocument({ ...docRef, chatId })}
      title={docRef.src ?? docRef.name}
    >
      <span className="doc-card-icon"><Icon name="file-text" size={18} /></span>
      <span className="doc-card-text">
        <span className="doc-card-name">{docRef.name}</span>
        <span className="doc-card-sub">{sub}</span>
      </span>
      <span className="doc-card-open"><Icon name="split" size={15} /></span>
    </button>
  )
}

/** Render attachment cards for a set of doc refs, deduped by identity. */
export function DocAttachments({ refs, chatId }: { refs: DocRef[]; chatId?: string }) {
  if (refs.length === 0) return null
  const seen = new Set<string>()
  const unique = refs.filter(r => {
    const k = docKey(r)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  return (
    <div className="doc-card-grid">
      {unique.map(r => <DocCard key={docKey(r)} docRef={r} chatId={chatId} />)}
    </div>
  )
}
